import { Company } from 'module/api/company/types';
import { PageType } from 'api/page-template/types';
import { CompanyPage } from 'api/pages/types';
import moment from 'moment';
import { useGetPagesByTypeListQuery } from 'queries/pages';
import { useMemo } from 'react';

const ACCOUNT_PAGE_TYPES = [
  PageType.Company,
  PageType.CompanyManual,
  PageType.ProjectNitro,
  PageType.ProjectManual,
  PageType.NitroExternal,
].join(',');

const PROJECT_PAGE_TYPES = [PageType.ProjectNitro, PageType.ProjectManual].join(
  ',',
);

interface Props {
  company: Company;
  projectId?: number;
  searchText: string;
}
interface Result {
  companyPages: CompanyPage[];
  allCompanyPages: CompanyPage[];
  isCompanyPagesLoading: boolean;
}
export const useCompanyPagesList = ({
  company,
  projectId,
  searchText,
}: Props): Result => {
  const isProjectContext = projectId != null;
  const sourceId = isProjectContext ? projectId : company.companyId;
  const objectType = isProjectContext ? 'PROJECT' : 'COMPANY';
  const pageTypeList = isProjectContext
    ? PROJECT_PAGE_TYPES
    : ACCOUNT_PAGE_TYPES;

  const { data: companyPages, isLoading } = useGetPagesByTypeListQuery(
    sourceId,
    pageTypeList,
    objectType,
  );

  // If we have data (even if it's an empty array), we're not loading anymore
  // isLoading is true only when there's no data AND the query is still fetching
  const isCompanyPagesLoading = isLoading && companyPages === undefined;

  const sortedCompanyPages = useMemo(() => {
    return (
      (companyPages as CompanyPage[] | undefined)?.sort((a, b) => {
        return moment(b.startTime).diff(moment(a.startTime));
      }) ?? []
    );
  }, [companyPages]);

  const filteredCompanyPages = useMemo(() => {
    return sortedCompanyPages.filter((companyPage) =>
      companyPage.title?.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [sortedCompanyPages, searchText]);

  return {
    companyPages: filteredCompanyPages,
    allCompanyPages: sortedCompanyPages,
    isCompanyPagesLoading,
  };
};
